<template>
  <v-main class="template-main">
    <div class="template-page">
      <router-link v-if="team" :to="{ name: 'teamedit', params: { id: teamId } }" class="back-link">
        <v-icon size="18">mdi-arrow-left</v-icon>
        {{ team.name }}
      </router-link>

      <v-progress-linear v-if="loading" color="primary" height="3" indeterminate></v-progress-linear>

      <div v-else-if="notFound" class="panel empty-state">
        <h1 class="empty-title">Template not found</h1>
        <p class="empty-text">It may have been deleted. Go back to the team page to see the team's templates.</p>
      </div>

      <template v-else-if="template">
        <div class="page-heading">
          <div>
            <span class="eyebrow">Practice template</span>
            <h1 class="page-title">{{ template.name || 'Untitled template' }}</h1>
            <p class="page-subtitle">
              Lay out the time slots and describe the kind of drill for each one. New practice plans can start from
              this layout.
            </p>
          </div>
        </div>

        <section class="panel">
          <div class="panel-header">Details</div>
          <div class="panel-body details">
            <v-text-field
                v-model="template.name"
                label="Template name"
                placeholder="Tuesday skills practice"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                :rules="[(v) => !!(v && v.trim()) || 'Enter a name']"
            ></v-text-field>
            <v-switch
                v-model="template.isDefault"
                color="primary"
                inset
                hide-details
                label="Default for new practice plans"
            ></v-switch>
          </div>
        </section>

        <section class="panel">
          <div class="panel-header">
            <span>Time slots</span>
            <span class="totals">
              <span class="mono">{{ totals.total }} min</span> total ·
              <span class="mono">{{ totals.drill }}</span> drills ·
              <span class="mono">{{ totals.breaks }}</span> breaks
            </span>
          </div>

          <div class="panel-body slots">
            <div v-if="template.timeSlots.length" class="slot-head" aria-hidden="true">
              <span>Start</span>
              <span>Minutes</span>
              <span>What to run</span>
              <span></span>
            </div>

            <div
                v-for="(slot, index) in template.timeSlots"
                :key="slot._key"
                class="slot"
                :class="{ 'slot-break': slot.isBreak }"
            >
              <span class="slot-start mono">{{ startLabel(index) }}</span>
              <v-text-field
                  v-model.number="slot.duration"
                  :aria-label="'Minutes for slot ' + (index + 1)"
                  type="number"
                  min="1"
                  :max="MAX_MINUTES"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  :rules="[minutesRule]"
                  class="slot-minutes"
              ></v-text-field>
              <div class="slot-description">
                <span v-if="slot.isBreak" class="break-label">
                  <v-icon size="18">mdi-cup-water</v-icon>
                  Water break
                </span>
                <v-text-field
                    v-else
                    v-model="slot.description"
                    :aria-label="'What to run in slot ' + (index + 1)"
                    placeholder="e.g. Fast-paced 2v1 to finish at the cage"
                    variant="outlined"
                    density="compact"
                    hide-details
                ></v-text-field>
              </div>
              <div class="slot-actions">
                <v-btn
                    icon="mdi-arrow-up"
                    size="small"
                    variant="text"
                    :disabled="index === 0"
                    :aria-label="'Move slot ' + (index + 1) + ' up'"
                    @click="move(index, -1)"
                ></v-btn>
                <v-btn
                    icon="mdi-arrow-down"
                    size="small"
                    variant="text"
                    :disabled="index === template.timeSlots.length - 1"
                    :aria-label="'Move slot ' + (index + 1) + ' down'"
                    @click="move(index, 1)"
                ></v-btn>
                <v-btn
                    icon="mdi-delete-outline"
                    size="small"
                    variant="text"
                    color="error"
                    :aria-label="'Remove slot ' + (index + 1)"
                    @click="removeSlot(index)"
                ></v-btn>
              </div>
            </div>

            <p v-if="!template.timeSlots.length" class="empty-text slots-empty">
              No time slots yet. Add the first one, for example a 10-minute warm-up.
            </p>

            <div class="add-row">
              <v-btn variant="outlined" color="primary" class="text-none" prepend-icon="mdi-plus" @click="addSlot">
                Add Time Slot
              </v-btn>
              <v-btn variant="outlined" class="text-none" prepend-icon="mdi-cup-water" @click="addBreak">
                Add Water Break
              </v-btn>
            </div>
          </div>
        </section>

        <div class="footer-actions">
          <v-btn color="error" variant="outlined" class="text-none" @click="confirmDelete = true">Delete Template</v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" class="text-none" :to="{ name: 'teamedit', params: { id: teamId } }">Cancel</v-btn>
          <v-btn color="primary" variant="flat" class="text-none" :loading="saving" :disabled="!canSave" @click="save">
            Save Template
          </v-btn>
        </div>
      </template>
    </div>

    <v-dialog v-model="confirmDelete" max-width="420">
      <v-card class="confirm-card">
        <v-card-title class="confirm-title">Delete this template?</v-card-title>
        <v-card-text>
          “{{ template && template.name }}” will be removed for everyone on the team. Practice plans that already
          started from it do not change.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" class="text-none" @click="confirmDelete = false">Keep It</v-btn>
          <v-btn color="error" variant="flat" class="text-none" @click="deleteTemplate">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-main>
</template>

<script>
import api from '../../api'

const MAX_MINUTES = 240;
let nextKey = 1;

function withKey(slot) {
  return {...slot, _key: nextKey++};
}

export default {
  name: 'PracticePlanTemplate',
  data() {
    return {
      MAX_MINUTES,
      team: null,
      template: null,
      loading: true,
      notFound: false,
      saving: false,
      confirmDelete: false,
    }
  },
  computed: {
    teamId() {
      return this.$route.params.teamid;
    },
    totals() {
      const slots = this.template ? this.template.timeSlots : [];
      let drill = 0;
      let breaks = 0;
      slots.forEach((s) => {
        const minutes = Number(s.duration) || 0;
        if (s.isBreak) {
          breaks += minutes;
        } else {
          drill += minutes;
        }
      });
      return {total: drill + breaks, drill, breaks};
    },
    canSave() {
      if (!this.template || !(this.template.name && this.template.name.trim())) {
        return false;
      }
      return this.template.timeSlots.every((s) => this.minutesRule(s.duration) === true);
    },
  },
  watch: {
    '$route.params': {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    load() {
      const {teamid, templateid} = this.$route.params;
      if (!teamid || !templateid) {
        return;
      }
      this.loading = true;
      this.notFound = false;

      api.teams.get(teamid, (team) => {
        this.team = team;
      });

      api.templates.get(teamid, templateid, (template) => {
        this.template = {
          ...template,
          isDefault: !!template.isDefault,
          timeSlots: (template.timeSlots || []).map(withKey),
        };
        this.loading = false;
      }, (err) => {
        this.loading = false;
        if (err.response && err.response.status === 404) {
          this.notFound = true;
        } else {
          this.$toast.open({message: 'Unable to load the template', type: 'error'});
        }
      });
    },
    minutesRule(value) {
      const n = Number(value);
      if (!Number.isInteger(n) || n < 1 || n > MAX_MINUTES) {
        return `1 to ${MAX_MINUTES}`;
      }
      return true;
    },
    startLabel(index) {
      let minutes = 0;
      for (let i = 0; i < index; i++) {
        minutes += Number(this.template.timeSlots[i].duration) || 0;
      }
      const h = Math.floor(minutes / 60);
      const m = String(minutes % 60).padStart(2, '0');
      return `${h}:${m}`;
    },
    addSlot() {
      this.template.timeSlots.push(withKey({duration: 15, isBreak: false, description: ''}));
    },
    addBreak() {
      const minutes = (this.team && this.team.waterBreakLength) || 5;
      this.template.timeSlots.push(withKey({duration: minutes, isBreak: true, description: ''}));
    },
    move(index, offset) {
      const slots = this.template.timeSlots;
      const target = index + offset;
      if (target < 0 || target >= slots.length) {
        return;
      }
      const [slot] = slots.splice(index, 1);
      slots.splice(target, 0, slot);
    },
    removeSlot(index) {
      this.template.timeSlots.splice(index, 1);
    },
    save() {
      const body = {
        id: this.template.id,
        name: this.template.name.trim(),
        isDefault: this.template.isDefault,
        timeSlots: this.template.timeSlots.map((s) => ({
          duration: Number(s.duration),
          isBreak: !!s.isBreak,
          description: s.isBreak ? '' : (s.description || '').trim(),
        })),
      };
      this.saving = true;
      api.templates.save(this.teamId, body, () => {
        this.saving = false;
        this.$toast.open({message: 'Template saved', type: 'success'});
      }, () => {
        this.saving = false;
        this.$toast.open({message: 'Unable to save the template', type: 'error'});
      });
    },
    deleteTemplate() {
      api.templates.delete(this.teamId, this.template.id, () => {
        this.confirmDelete = false;
        this.$toast.open({message: 'Template deleted', type: 'success'});
        this.$router.push({name: 'teamedit', params: {id: this.teamId}});
      }, () => {
        this.confirmDelete = false;
        this.$toast.open({message: 'Unable to delete the template', type: 'error'});
      });
    },
  },
}
</script>

<style scoped>
.template-main {
  background: var(--surface-sunken);
  min-height: 100vh;
}

.template-page {
  max-width: 960px;
  margin: 0 auto;
  padding: var(--space-6) 32px 48px;
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  width: fit-content;
  font-size: 14px;
  font-weight: 500;
  color: var(--brand-primary);
}

.eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.page-title {
  margin: var(--space-1) 0 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--ink);
}

.page-subtitle {
  margin: var(--space-1) 0 0;
  max-width: 60ch;
  font-size: 14px;
  color: var(--ink-muted);
}

.panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  padding: var(--space-3) var(--space-5);
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
}

.panel-body {
  padding: var(--space-5);
}

.details {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--space-5);
  align-items: center;
}

.totals {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-muted);
}

.mono {
  font-family: var(--font-mono);
}

.slots {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.slot-head,
.slot {
  display: grid;
  grid-template-columns: 56px 110px minmax(0, 1fr) auto;
  gap: var(--space-3);
  align-items: center;
}

.slot-head {
  padding: 0 var(--space-3);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ink-muted);
}

.slot {
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  background: var(--surface);
}

.slot-break {
  border-style: dashed;
  border-color: var(--border);
  background: var(--surface-sunken);
}

.slot-start {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
}

.slot-description {
  min-width: 0;
}

.break-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 14px;
  font-weight: 500;
  color: var(--ink-muted);
}

.slot-actions {
  display: flex;
  gap: 2px;
}

.slots-empty {
  padding: var(--space-4) 0;
  text-align: center;
}

.add-row {
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
  padding-top: var(--space-3);
}

.footer-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.empty-state {
  padding: var(--space-8) var(--space-6);
  text-align: center;
}

.empty-title {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--ink);
}

.empty-text {
  margin: var(--space-2) 0 0;
  font-size: 14px;
  color: var(--ink-muted);
}

.confirm-card {
  border-radius: var(--radius-md) !important;
}

.confirm-title {
  font-weight: 600;
}

@media (max-width: 700px) {
  .template-page {
    padding: var(--space-5) var(--space-4) 40px;
  }

  .details {
    grid-template-columns: minmax(0, 1fr);
  }

  .slot-head {
    display: none;
  }

  .slot {
    grid-template-columns: 48px 96px minmax(0, 1fr);
  }

  .slot-actions {
    grid-row: 1;
    grid-column: 3;
    justify-self: end;
  }

  .slot-description {
    grid-column: 1 / -1;
  }
}
</style>
